/**
 * Sentry Observability Provider
 *
 * Plugs Sentry into the shared ObservabilityManager on both platforms. Shared
 * has no Sentry dependency: each app passes in its own SDK module
 * (`@sentry/react` on web, `@sentry/react-native` on mobile), which both
 * satisfy {@link SentryClient} because they expose the same core API.
 *
 * Everything arriving here is already scrubbed by the manager (query strings,
 * sensitive keys, users reduced to their id). The error object is passed to
 * `captureException` as is, so Sentry reads its name, message and stack; the
 * apps must not enable `extraErrorDataIntegration`, which would also send the
 * typed error's own fields (its full `url`, the server's response body).
 *
 * | Manager call     | Sentry                                              |
 * | ---------------- | --------------------------------------------------- |
 * | `captureError`   | `captureException(error, { level, tags, contexts })` |
 * | `captureMessage` | `captureMessage(message, { level, tags, contexts })` |
 * | `recordMetric`   | a `metric` breadcrumb (EAS Observe owns metrics)     |
 * | `addBreadcrumb`  | `addBreadcrumb`                                      |
 * | `setUser`        | `setUser({ id })`                                    |
 * | `setTag`         | `setTag`                                             |
 * | `setContext`     | `setContext`; `clearContext` nulls each one set      |
 */

import type {
  ObservabilityBreadcrumb,
  ObservabilityError,
  ObservabilityEvent,
  ObservabilityMetric,
  ObservabilityProvider,
  ObservabilitySeverity,
  ObservabilityUser,
} from '../../../types/observability';

/** Sentry's severity levels; ours are a subset with the same names. */
export type SentryLevel =
  'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

export interface SentryCaptureContext {
  level?: SentryLevel;
  tags?: Record<string, string>;
  contexts?: Record<string, Record<string, unknown>>;
}

export interface SentryBreadcrumb {
  message?: string;
  category?: string;
  level?: SentryLevel;
  /** Seconds since the epoch, as Sentry expects. */
  timestamp?: number;
  data?: Record<string, unknown>;
}

/** The slice of a Sentry SDK module this provider calls. */
export interface SentryClient {
  captureException(exception: unknown, context?: SentryCaptureContext): string;
  captureMessage(message: string, context?: SentryCaptureContext): string;
  addBreadcrumb(breadcrumb: SentryBreadcrumb): void;
  setUser(user: { id: string } | null): void;
  setTag(key: string, value: string): void;
  setContext(name: string, context: Record<string, unknown> | null): void;
}

/** Context sent with an event, under one Sentry context named `details`. */
function captureContext(
  severity: ObservabilitySeverity,
  context?: Record<string, unknown>,
  tags?: Record<string, string>
): SentryCaptureContext {
  return {
    level: severity,
    tags,
    contexts: context ? { details: context } : undefined,
  };
}

export class SentryObservabilityProvider implements ObservabilityProvider {
  private readonly contextNames = new Set<string>();

  constructor(private readonly sentry: SentryClient) {}

  captureError({ error, severity, context, tags }: ObservabilityError): void {
    this.sentry.captureException(
      error,
      captureContext(severity, context, tags)
    );
  }

  captureMessage({
    message,
    severity,
    context,
    tags,
  }: ObservabilityEvent): void {
    this.sentry.captureMessage(
      message,
      captureContext(severity, context, tags)
    );
  }

  recordMetric({ name, value, unit, tags }: ObservabilityMetric): void {
    this.sentry.addBreadcrumb({
      category: 'metric',
      message: name,
      level: 'info',
      data: { value, unit, ...tags },
    });
  }

  addBreadcrumb({
    message,
    category,
    level,
    timestamp,
    data,
  }: ObservabilityBreadcrumb): void {
    this.sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: timestamp === undefined ? undefined : timestamp / 1000,
      data,
    });
  }

  setUser(user: ObservabilityUser | null): void {
    this.sentry.setUser(user === null ? null : { id: user.id });
  }

  setTag(key: string, value: string): void {
    this.sentry.setTag(key, value);
  }

  setContext(name: string, context: Record<string, unknown>): void {
    this.contextNames.add(name);
    this.sentry.setContext(name, context);
  }

  clearContext(): void {
    for (const name of this.contextNames) {
      this.sentry.setContext(name, null);
    }
    this.contextNames.clear();
  }
}
